/**
 * M3X.WORKSPACE-CLOSURE Session 2.6 PR 2.C — pulsing chevron hint
 * that auto-shows on the Rail 2 collapse chevron for the first 3
 * page navigations of a session, then never again.
 *
 * Persistence: localStorage key `solvait.workspace.chevronHintCount.{userId}`
 * (integer). Increments by 1 per session-mount when count < 3.
 *
 * Visual spec per closure-hotfix S2.6 §2.C:
 *   bg #1e293b · text white · 11px · padding 6px 10px · radius 6px
 *   position: top 4px · inset-inline-start calc(100% + 8px) (logical)
 *   subtle scale pulse 1.0 → 1.02 → 1.0 every 2s
 *
 * Auto-dismiss after 4s. No render after count >= 3.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";


export const CHEVRON_HINT_MAX_DISPLAYS = 3;


function _countKey(userId: string | null): string {
  return `solvait.workspace.chevronHintCount.${userId ?? "anon"}`;
}


export interface ChevronHintTooltipProps {
  userId: string | null;
}


export function ChevronHintTooltip({ userId }: ChevronHintTooltipProps) {
  const { t } = useTranslation();

  // Initial visibility decision happens ONCE per mount. If the
  // counter is already at 3+, never show.
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const current = parseInt(
        window.localStorage.getItem(_countKey(userId)) ?? "0",
        10,
      );
      if (current < CHEVRON_HINT_MAX_DISPLAYS) {
        // Increment immediately so a fast re-mount doesn't double-show
        window.localStorage.setItem(
          _countKey(userId),
          String(current + 1),
        );
        return true;
      }
      return false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setVisible(false), 4_000);
    return () => clearTimeout(timer);
  }, [visible]);

  if (!visible) return null;

  return (
    <span
      role="tooltip"
      data-chevron-hint-tooltip
      className="pointer-events-none absolute z-20 whitespace-nowrap"
      style={{
        top: "4px",
        insetInlineStart: "calc(100% + 8px)",
        backgroundColor: "#1e293b",
        color: "white",
        fontSize: "11px",
        padding: "6px 10px",
        borderRadius: "6px",
        animation: "chevronHintPulse 2s ease-in-out infinite",
      }}
    >
      {t(
        "workspace.rail2.collapse.hint",
        "← collapse the rail · or press [",
      )}
    </span>
  );
}


export default ChevronHintTooltip;
